type Done = { html?: string; usage?: string; total?: string };
type OllamaMessage = { role: string; content: string; tool_calls?: OllamaToolCall[]; tool_name?: string };
type OllamaToolCall = { function: { name: string; arguments: Record<string, unknown> } };
type OllamaChunk = { message?: { content?: string; tool_calls?: OllamaToolCall[] }; done?: boolean; prompt_eval_count?: number; eval_count?: number; error?: string };

const MAX_TOOL_ROUNDS = 12;

/**
 * The in-app AI chat. Normally the server runs the model and streams the answer (Server-Sent Events over a POST).
 * In "ollama" mode the model runs on the person's own computer: this component talks to their Ollama directly and
 * asks the server to run the archive tools (as the person, filtered there). Either way the answer is plain text
 * while it streams, then the server's sanitized HTML replaces it.
 */
export class P9Chat extends HTMLElement {
  #log!: HTMLElement;
  #form: HTMLFormElement | null = null;
  #busy = false;
  #atEnd = true;

  connectedCallback(): void {
    this.#log = this.querySelector<HTMLElement>(".chat-log")!;
    this.#form = this.querySelector<HTMLFormElement>("form.chat-form");
    // Stay at the newest message when the log's box changes size (e.g. the header is measured after load),
    // unless the person has scrolled up to read.
    this.#log.addEventListener("scroll", () => {
      this.#atEnd = this.#log.scrollHeight - this.#log.clientHeight - this.#log.scrollTop < 8;
    });
    new ResizeObserver(() => {
      if (this.#atEnd) this.#scroll();
    }).observe(this.#log);
    if (!this.#form) return;
    const textarea = this.#form.querySelector("textarea")!;
    this.#form.addEventListener("submit", (event) => {
      event.preventDefault();
      void this.#send(textarea);
    });
    textarea.addEventListener("keydown", (event) => {
      if (event.key === "Enter" && !event.shiftKey && !event.isComposing) {
        event.preventDefault();
        void this.#send(textarea);
      }
    });
    this.#scroll();
  }

  async #send(textarea: HTMLTextAreaElement): Promise<void> {
    const message = textarea.value.trim();
    if (!message || this.#busy) return;
    this.#busy = true;
    this.#form!.classList.add("busy");
    textarea.value = "";

    this.#append("user").append(Object.assign(document.createElement("p"), { textContent: message }));
    const answer = this.#append("assistant");
    answer.classList.add("streaming");
    const steps = document.createElement("ul");
    steps.className = "chat-steps";
    const text = document.createElement("div");
    text.className = "chat-text";
    answer.append(steps, text);
    this.#scroll();

    const finish = (data: Done) => {
      answer.classList.remove("streaming");
      text.classList.add("prose");
      text.innerHTML = data.html ?? "";
      if (data.usage) answer.append(Object.assign(document.createElement("p"), { className: "chat-usage", textContent: data.usage }));
      const total = document.querySelector<HTMLElement>("[data-chat-total]");
      if (total && data.total) total.textContent = `This chat: ${data.total}`;
      const title = document.querySelector<HTMLElement>(".page-head h1");
      if (title?.textContent === "New chat") title.textContent = message.length > 80 ? message.slice(0, 79) + "…" : message;
    };

    try {
      if (this.dataset.mode === "ollama") {
        finish(await this.#runLocal(message, steps, text));
        return;
      }
      const token = this.#token();
      const response = await fetch(this.dataset.sendUrl!, {
        method: "POST",
        headers: { "Content-Type": "application/json", RequestVerificationToken: token, Accept: "text/event-stream" },
        body: JSON.stringify({ message }),
      });
      if (!response.ok || !response.body) {
        const result = (await response.json().catch(() => ({}))) as { error?: string };
        throw new Error(result.error ?? "The message couldn't be sent.");
      }
      await this.#read(response.body, (kind, data) => {
        if (kind === "text") text.textContent += data.text ?? "";
        else if (kind === "tool") steps.append(Object.assign(document.createElement("li"), { textContent: data.text ?? "" }));
        else if (kind === "error") throw new Error(data.text ?? "Something went wrong.");
        else if (kind === "done") finish(data);
        this.#scroll();
      });
    } catch (error) {
      answer.classList.remove("streaming");
      answer.classList.add("failed");
      answer.append(Object.assign(document.createElement("p"), { className: "error", textContent: (error as Error).message }));
      textarea.value = textarea.value || message;
    } finally {
      this.#busy = false;
      this.#form!.classList.remove("busy");
      textarea.focus();
    }
  }

  #token(): string {
    return this.querySelector<HTMLInputElement>('input[name="__RequestVerificationToken"]')?.value ?? "";
  }

  async #post<T>(path: string, body: unknown): Promise<T> {
    const response = await fetch(`${this.dataset.localUrl}/${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", RequestVerificationToken: this.#token() },
      body: JSON.stringify(body),
    });
    const result = (await response.json().catch(() => ({}))) as T & { error?: string };
    if (!response.ok) throw new Error(result.error ?? "The archive refused the request.");
    return result;
  }

  // The model runs in the person's own Ollama; the archive saves the conversation and runs the tools.
  async #runLocal(message: string, steps: HTMLElement, text: HTMLElement): Promise<Done> {
    const ollama = (this.dataset.ollama ?? "http://localhost:11434").replace(/\/$/, "");
    const start = await this.#post<{ instructions: string; tools: unknown[]; history: OllamaMessage[] }>("start", { message });
    const messages: OllamaMessage[] = [{ role: "system", content: start.instructions }, ...start.history];
    let input = 0;
    let output = 0;
    let answer = "";

    for (let round = 0; round < MAX_TOOL_ROUNDS; round++) {
      let response: Response;
      try {
        response = await fetch(`${ollama}/api/chat`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ model: this.dataset.model, messages, tools: start.tools, stream: true }),
        });
      } catch {
        throw new Error(`Couldn't reach Ollama at ${ollama}. Is it running, is OLLAMA_ORIGINS set to ${location.origin}, and did you let this site connect to your computer?`);
      }
      if (!response.ok || !response.body) throw new Error(`Ollama answered ${response.status}: ${(await response.text()).slice(0, 200)}`);

      let content = "";
      const calls: OllamaToolCall[] = [];
      await this.#readLines(response.body, (chunk: OllamaChunk) => {
        if (chunk.error) throw new Error(`Ollama: ${chunk.error}`);
        content += chunk.message?.content ?? "";
        calls.push(...(chunk.message?.tool_calls ?? []));
        text.textContent = content;
        if (chunk.done) {
          input += chunk.prompt_eval_count ?? 0;
          output += chunk.eval_count ?? 0;
        }
        this.#scroll();
      });
      answer = content;
      if (calls.length === 0) break;

      messages.push({ role: "assistant", content, tool_calls: calls });
      for (const call of calls) {
        const tool = await this.#post<{ result: string; label: string }>("tool", { name: call.function.name, arguments: call.function.arguments ?? {} });
        steps.append(Object.assign(document.createElement("li"), { textContent: tool.label }));
        messages.push({ role: "tool", tool_name: call.function.name, content: tool.result });
      }
      text.textContent = "";
    }
    return this.#post<Done>("finish", { content: answer, inputTokens: input, outputTokens: output });
  }

  // Ollama streams one JSON object per line.
  async #readLines(body: ReadableStream<Uint8Array>, handle: (chunk: OllamaChunk) => void): Promise<void> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let end;
      while ((end = buffer.indexOf("\n")) >= 0) {
        const line = buffer.slice(0, end).trim();
        buffer = buffer.slice(end + 1);
        if (line) handle(JSON.parse(line) as OllamaChunk);
      }
    }
    if (buffer.trim()) handle(JSON.parse(buffer) as OllamaChunk);
  }

  // Parses "event: x\ndata: {...}\n\n" blocks as they arrive.
  async #read(body: ReadableStream<Uint8Array>, handle: (kind: string, data: { text?: string; html?: string; usage?: string; total?: string }) => void): Promise<void> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let end;
      while ((end = buffer.indexOf("\n\n")) >= 0) {
        const block = buffer.slice(0, end);
        buffer = buffer.slice(end + 2);
        const kind = /^event: (.*)$/m.exec(block)?.[1] ?? "message";
        const data = /^data: (.*)$/m.exec(block)?.[1];
        if (data) handle(kind, JSON.parse(data));
      }
    }
  }

  #append(role: "user" | "assistant"): HTMLElement {
    const div = document.createElement("div");
    div.className = `chat-msg ${role}`;
    this.#log.append(div);
    return div;
  }

  #scroll(): void {
    // When the chat fills the page box the log scrolls on its own; otherwise (phones) the page does.
    if (this.#log.scrollHeight > this.#log.clientHeight) this.#log.scrollTop = this.#log.scrollHeight;
    // scrollTo, not scrollIntoView: that would also scroll the workspace around a frame and push its top bar away.
    else window.scrollTo(0, document.documentElement.scrollHeight);
  }
}

customElements.define("p9-chat", P9Chat);
